import { createBrowserHistory } from 'history';
// I18n
import BasicI18nSetup from 'I18n/init/setup/BasicI18nSetup';
import lang from 'i2l?query=sporks!../lang/en.lyaml';
import { getIdentificationToken } from 'pricing-pages-lib/api/IdentificationTokenApi';
import Raven from 'raven-js';
// Redux
import { Provider } from 'react-redux';
// Data fetching client
import { DataFetchingClientProvider } from 'data-fetching-client';
import { GET_SEATS_INVENTORY } from 'pricing-pages-api/seats-inventory/queries';
import { GET_ACTIVE_TRIALS } from 'pricing-pages-api/trials/queries';
import { PHONE_NUMBER, SEATS_INVENTORY } from 'pricing-pages-lib/constants/EarlyRequesterNames';
import { dataFetchingClient } from './utils/dataFetchingClient';
// Rhumb
import enviro from 'enviro';
import { Application } from 'foundations-components/components/Application';
import { FloatingAlertList } from 'foundations-components/transitional/components/FloatingAlertList';
import { GET_PHONE_NUMBER } from 'pricing-pages-api/phone-number/queries';
import { EN } from 'pricing-pages-lib/constants/Languages';
import { getPreferredLanguage } from 'pricing-pages-lib/utils/getPreferredLanguage';
import { getInAppLegacyPurchaseMotionsResponse } from 'pricing-pages-api/purchase-motions/getInAppLegacyPurchaseMotionsResponse';
import getUsageTracker from 'pricing-pages-lib/utils/getUsageTracker';
import { renderWithNavCopilot } from 'quartz-core/client-with-copilot';
import { RhumbProvider } from 'react-rhumb';
import historyAdapter from 'react-rhumb/historyAdapterV4';
import { Router } from 'react-router-dom';
import { setPrimaryTracker } from 'usage-tracker-container';
import rhumbConfig from '../rhumb-config.yaml';
import Routes from './Routes';
import { startApp } from './actions/AppActions';
import { getAuth } from './actions/AuthActions';
import { storeEnvironmentConfig } from './actions/ConfigActions';
import { fetchExperiments } from './actions/ExperimentationActions';
import { fetchMarketingContacts } from './actions/MarketingContactsActions';
import { fetchNumContactsInCrm } from './actions/NumContactsInCrmActions';
import { fetchProductInventoryData } from './actions/ProductInventoryActions';
import { fetchProducts } from './actions/ProductsActions';
import { fetchPromotions } from './actions/PromotionActions';
import { fetchPurchaseMotionData } from './actions/PurchaseMotionActions';
import { fetchRequiredOnboarding } from './actions/RequiredOnboardingActions';
import AsyncDevGateToggle from './components/dev-gate-toggle/AsyncDevGateToggle';
import { DefaultFallbackComponent } from './components/error-boundary/DefaultFallbackComponent';
import ErrorBoundary from './components/error-boundary/ErrorBoundary';
import { ERROR_SEVERITY } from './components/error-boundary/error-severity';
import { AUTHED_APP } from './constants/ErrorBoundaryNames';
import { CurrencyContextProvider } from './context/CurrencyContext';
import { GlobalLoadingStateContextProvider } from './context/GlobalLoadingStateContext';
import { AuthedI18nContext } from './context/I18nContext';
import { hydrateUpgradePageContext } from './reducers/UpgradePageView';
import { isBillingAdminSelector } from './selectors/AuthSelectors';
import { isHubSpotDotComAuthedUserRedirect } from './utils/appUtils';
import configureStore from './utils/configureStore';
import { Metrics, setupMetricsFactory } from './utils/metrics';
import { createUsageTracker } from './utils/tracking/_UsageTracker';
import { trackCommunicationMethodsInteraction } from './utils/tracking/trackingEventUtils';
import { getRootUrl } from './utils/urls/getRootUrl';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export default class App {
  constructor(config) {
    this.store = configureStore();
    this.tracker = createUsageTracker();
    this.config = config;
  }

  // Kicks off the app. This is intentionally not in the constructor.
  async start() {
    setupMetricsFactory('pricing-pages-ui');
    this.loadConversationsScript();
    this.store.dispatch(startApp());
    enviro.setDebug('laboratory-lib', true);
    const {
      user,
      portal
    } = await this.store.dispatch(getAuth());
    await BasicI18nSetup.setup({
      lang,
      user,
      portal
    });
    this.setupEventTracker();

    // Without setTimeout here so these requests are kicked off before the iframe is loaded so they do not hit CORS
    // and do end up generating OPTIONS requests. This setTimeout attempts to delay the fetches just long enough for it to return.
    setTimeout(() => {
      try {
        // this.displayLoading();
        this.setupSentry();
        this.startRouter(BasicI18nSetup.provider);
      } catch (__error) {
        // We chain our bootstrapping code with this catch
        // so we can show an Error screen rather than just a blank page
        // A side effect of this is that any errors that bubble up here
        // would be swallowed up unless we re-throw.
        this.startRouter(BasicI18nSetup.provider, true);
        // Raven.captureException(new Error('Failed to bootstrap in-app'), {
        //   extra: { error },
        // });
      }
    }, 0);
  }
  setupEventTracker() {
    setPrimaryTracker(this.tracker);
    const state = this.store.getState();

    // Required default event properties needs to be set here if we want event tracking on App level - otherwise, it will be undefined and cause usage-tracker-js event error. Properties should not include data from non-blocking fetches as it may not be available when this is called.
    getUsageTracker().setProperties({
      isBillingAdmin: isBillingAdminSelector(state),
      inAppOrPublic: 'in-app',
      isDotComAuthedRedirect: isHubSpotDotComAuthedUserRedirect()
    });
  }
  loadConversationsScript() {
    // Set the HubSpot Conversations settings
    window.hsConversationsSettings = {
      loadImmediately: false
    };

    // Dynamically create and append the script
    const isQa = enviro.isQa();
    const qaSuffix = isQa ? 'qa' : '';
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.id = 'hubspot-messages-loader';
    script.async = true;
    script.defer = true;
    script.src = `//js.usemessages${qaSuffix}.com/conversations-embed.js`;
    script.setAttribute('data-hsjs-env', enviro.getShort());
    script.setAttribute('data-hsjs-portal', isQa ? '100048175' : '53');
    document.body.appendChild(script);
  }
  setupSentry() {
    const state = this.store.getState();
    const {
      user_id,
      email
    } = state.auth.user;
    if (user_id && email) Raven.setUserContext({
      id: user_id.toString(),
      email
    });
  }

  // Important Note! Any promises added to this group need to have an internal
  // catch clause that will log to Sentry. Take a look at dispatchAndThrowError()
  // and sentryMiddleware for more info on how these promises do it
  fetchNonAppBlockingPricingAndPurchasingData() {
    const reduxFetchRequests = [fetchPurchaseMotionData({
      getPurchaseMotions: () => getInAppLegacyPurchaseMotionsResponse(['MERCH_CARD', 'CALCULATOR'])
    }), fetchProducts(), fetchPromotions(), fetchNumContactsInCrm(), fetchMarketingContacts(), fetchRequiredOnboarding(), fetchProductInventoryData(), fetchExperiments()].map(fetchRequest => this.store.dispatch(fetchRequest));
    const preferredLanguage = getPreferredLanguage() || EN;
    const dataFetchingClientFetchRequests = [dataFetchingClient.query({
      query: GET_SEATS_INVENTORY,
      variables: {
        earlyRequestName: SEATS_INVENTORY
      }
    }), dataFetchingClient.query({
      query: GET_ACTIVE_TRIALS
    }), dataFetchingClient.query({
      query: GET_PHONE_NUMBER,
      variables: {
        earlyRequestName: PHONE_NUMBER,
        preferredLanguage
      }
    })];
    return Promise.all([...reduxFetchRequests, ...dataFetchingClientFetchRequests]);
  }

  /**
   * @see {@link https://git.hubteam.com/HubSpot/conversations-visitor/blob/879af5a94b04d87ddf2f6b4dc794d3421bf6a237/docs/external-js-api/ApiReference.md}
   */
  async setupChat() {
    await this.initializeChatSettings();
    if (window.HubSpotConversations) {
      this.onChatWidgetReady();
    } else {
      window.hsConversationsOnReady = [this.onChatWidgetReady];
    }
  }
  async initializeChatSettings() {
    const state = this.store.getState();
    const {
      email
    } = state.auth && state.auth.user;
    try {
      const resp = await getIdentificationToken();
      if (!window.hsConversationsSettings) {
        window.hsConversationsSettings = {
          loadImmediately: false
        };
      }
      const token = resp.token;
      if (email && token) {
        window.hsConversationsSettings.identificationEmail = email;
        window.hsConversationsSettings.identificationToken = token;
      }
    } catch (error) {
      Raven.captureException(error, {
        fingerprint: ['failed-to-initialize-chat-settings']
      });
    }
  }
  onChatWidgetReady() {
    const status = window.HubSpotConversations.widget.status();
    if (!status || !status.pending) {
      window.HubSpotConversations.widget.load();
    }
    this.addChatWidgetEventTracking();
  }
  addChatWidgetEventTracking() {
    trackCommunicationMethodsInteraction({
      action: 'chat-conversation-loaded',
      trackingSource: 'in-app'
    });
    Metrics.counter('live-chat-loaded', {
      location: 'in-app'
    }).increment();

    /**
     * Conversation started means the user sent a message
     * API reference {@see https://git.hubteam.com/HubSpot/conversations-visitor/blob/master/docs/external-js-api/ApiReference.md}
     */
    window.HubSpotConversations.on('conversationStarted', payload => {
      trackCommunicationMethodsInteraction({
        action: 'chat-conversation-started',
        trackingSource: 'in-app',
        conversationId: payload.conversation.conversationId
      });
      Metrics.counter('live-chat-interaction', {
        location: 'in-app'
      }).increment();
    });
  }
  startRouter(i18nLoader, __hasError = false) {
    this.store.dispatch(storeEnvironmentConfig(this.config));
    this.fetchNonAppBlockingPricingAndPurchasingData().then(() => {
      //temp logic to avoid duplicate fetches to hydrate the upgrade page context
      this.store.dispatch(hydrateUpgradePageContext());
    }).catch(error => {
      Raven.captureException(error, {
        fingerprint: ['context-setup-error']
      });
      console.error(error);
    });
    const history = createBrowserHistory({
      basename: getRootUrl()
    });
    this.setupChat().catch(error => {
      Raven.captureException(error, {
        fingerprint: ['failed-to-setup-chat']
      });
      Metrics.counter('live-chat-failed').increment();
    });
    const DevGateToggleComponent = enviro.isQa() ? AsyncDevGateToggle : null;
    renderWithNavCopilot( /*#__PURE__*/_jsxs(Application, {
      name: "pricing-pages-ui",
      children: [/*#__PURE__*/_jsx(FloatingAlertList, {}), /*#__PURE__*/_jsx(DataFetchingClientProvider, {
        client: dataFetchingClient,
        children: /*#__PURE__*/_jsx(Provider, {
          store: this.store,
          children: /*#__PURE__*/_jsx(AuthedI18nContext.Provider, {
            value: {
              i18nLoader: i18nLoader
            },
            children: /*#__PURE__*/_jsx(RhumbProvider, {
              history: historyAdapter(history)
              // Update type here
              ,
              config: rhumbConfig,
              children: /*#__PURE__*/_jsx(GlobalLoadingStateContextProvider, {
                children: /*#__PURE__*/_jsx(Router, {
                  history: history,
                  children: /*#__PURE__*/_jsx(CurrencyContextProvider, {
                    children: /*#__PURE__*/_jsxs(ErrorBoundary, {
                      errorBoundaryName: AUTHED_APP,
                      FallbackComponent: DefaultFallbackComponent,
                      severity: ERROR_SEVERITY.HIGH,
                      children: [/*#__PURE__*/_jsx(Routes, {}), DevGateToggleComponent && /*#__PURE__*/_jsx(DevGateToggleComponent, {})]
                    })
                  })
                })
              })
            })
          })
        })
      })]
    }), {
      layoutType: 'sidebar'
    });
  }
}